import { useState, useEffect, useRef } from "react";
import { Container, Card, Button, Spinner } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { FaCheckCircle, FaTimesCircle, FaShoppingCart, FaReceipt, FaStar } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import "./PaymentSuccess.css";

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  
  const [isLoading, setIsLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [error, setError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const animationTriggered = useRef(false);

  const transactionId = searchParams.get("transactionId");
  const userID = searchParams.get("userID") || searchParams.get("userId");

  useEffect(() => {
    if (transactionId && userID) {
      checkPaymentAndCreateOrder();
    } else {
      setError("Missing payment information");
      setIsLoading(false);
    }
  }, [transactionId, userID]);

  const fireConfetti = () => {
    // Create confetti elements using CSS
    const confettiContainer = document.createElement('div');
    confettiContainer.className = 'confetti-container';
    confettiContainer.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 9999;
      overflow: hidden;
    `;
    document.body.appendChild(confettiContainer);

    const colors = ['#4CAF50', '#81C784', '#FFD700', '#FFA500', '#FF6B6B', '#4ECDC4', '#A5D6A7'];
    
    for (let i = 0; i < 100; i++) {
      const confetti = document.createElement('div');
      const color = colors[Math.floor(Math.random() * colors.length)];
      const left = Math.random() * 100;
      const animationDelay = Math.random() * 3;
      const size = Math.random() * 10 + 5;
      
      confetti.style.cssText = `
        position: absolute;
        width: ${size}px;
        height: ${size}px;
        background: ${color};
        left: ${left}%;
        top: -20px;
        border-radius: ${Math.random() > 0.5 ? '50%' : '0'};
        animation: confettiFall ${3 + Math.random() * 2}s linear ${animationDelay}s forwards;
        transform: rotate(${Math.random() * 360}deg);
      `;
      confettiContainer.appendChild(confetti);
    }

    // Add keyframes if not exists
    if (!document.getElementById('confetti-keyframes')) {
      const style = document.createElement('style');
      style.id = 'confetti-keyframes';
      style.textContent = `
        @keyframes confettiFall {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
      `;
      document.head.appendChild(style);
    }

    // Remove confetti after animation
    setTimeout(() => {
      confettiContainer.remove();
    }, 6000);
  };

  const animateSuccess = () => {
    if (animationTriggered.current) return;
    animationTriggered.current = true;
    
    setShowSuccess(true);
    
    // Trigger confetti after a small delay
    setTimeout(() => {
      fireConfetti();
    }, 300);
  };

  const checkPaymentAndCreateOrder = async () => {
    try {
      setIsLoading(true);
      
      const token = localStorage.getItem("token");
      
      if (!transactionId || !userID) {
        setError("Missing payment information");
        setIsLoading(false);
        return;
      }

      // Check payment status from backend
      const response = await fetch(
        `https://madhusewingmachines.com/api/phonepe/checkPayment/${transactionId}/${userID}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const result = await response.json();
      console.log("Payment status result:", result);

      if (result.success) {
        const paymentState = result.success.status || result.success.state;
        setPaymentStatus(paymentState);

        if (paymentState === "COMPLETED" || paymentState === "SUCCESS") {
          // Payment successful - create order
          const pendingOrderStr = localStorage.getItem("pendingOrder");
          
          if (pendingOrderStr) {
            const pendingOrder = JSON.parse(pendingOrderStr);
            
            // Ensure the order data has all required fields
            const orderData = {
              ...pendingOrder,
              status: "Confirmed",
              paymentStatus: "Paid",
              transactionId: transactionId
            };

            console.log("Creating order with data:", orderData);

            const orderResponse = await fetch("https://madhusewingmachines.com/api/orders", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify(orderData),
            });

            const orderResult = await orderResponse.json();
            console.log("Order creation result:", orderResult);

            if (orderResult.success) {
              localStorage.removeItem("pendingOrder");
              localStorage.removeItem("pendingTransactionId");
              clearCart();
              setIsLoading(false);
              animateSuccess();
              return;
            } else {
              console.error("Order creation failed:", {
                error: orderResult.message || orderResult.error,
                errors: orderResult.errors,
                transactionId: transactionId,
                orderData: orderData
              });
              setError(`Order creation failed: ${orderResult.message || orderResult.error || 'Unknown error'}. Please contact support with transaction ID: ${transactionId}`);
            }
          } else {
            // Order might already be created
            setIsLoading(false);
            animateSuccess();
            return;
          }
        } else if (paymentState === "FAILED" || paymentState === "FAILURE") {
          setError("Payment failed. Please try again.");
        } else {
          setError(`Payment status: ${paymentState}. Please wait or contact support.`);
        }
      } else {
        setError(result.error || "Failed to verify payment");
      }
    } catch (err) {
      console.error("Error checking payment:", err);
      setError("Failed to verify payment. Please contact support.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinueShopping = () => {
    navigate("/");
  };

  const handleViewOrders = () => {
    navigate("/OrderHistory");
  };

  const handleRetry = () => {
    navigate("/checkout");
  };

  if (isLoading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "80vh" }}>
        <Card className="text-center p-5 shadow border-0" style={{ maxWidth: "500px" }}>
          <Card.Body>
            <Spinner animation="border" variant="success" className="mb-4" style={{ width: "3rem", height: "3rem" }} />
            <h4 className="mb-3">Verifying Payment...</h4>
            <p className="text-muted">Please wait while we confirm your payment</p>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "80vh" }}>
        <Card className="text-center p-5 shadow border-0" style={{ maxWidth: "500px" }}>
          <Card.Body>
            <div className="error-icon-container mb-4">
              <FaTimesCircle size={80} color="#f44336" />
            </div>
            <h3 className="text-danger mb-3">Payment Issue</h3>
            <p className="text-muted mb-4">{error}</p>
            <div className="d-grid gap-2">
              <Button variant="success" size="lg" onClick={handleRetry}>
                Try Again
              </Button>
              <Button variant="outline-secondary" onClick={handleContinueShopping}>
                Go to Home
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "80vh" }}>
      <AnimatePresence>
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          >
            <Card className="text-center p-5 shadow-lg border-0 success-card" style={{ maxWidth: "550px" }}>
              <Card.Body>
                {/* Animated checkmark */}
                <motion.div 
                  className="success-animation"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 10 }}
                >
                  <div className="checkmark-circle">
                    <div className="checkmark-circle-bg"></div>
                    <div className="checkmark-ring"></div>
                    <div className="checkmark-ring ring-2"></div>
                    <div className="checkmark-ring ring-3"></div>
                    <motion.div
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
                    >
                      <FaCheckCircle className="checkmark-icon" size={80} />
                    </motion.div>
                  </div>
                </motion.div>

                {/* Sparkles */}
                <div className="sparkles">
                  <motion.div
                    animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1.2, 0.8], rotate: [0, 180, 360] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <FaStar className="sparkle sparkle-1" />
                  </motion.div>
                  <motion.div
                    animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1.2, 0.8], rotate: [0, 180, 360] }}
                    transition={{ duration: 2, repeat: Infinity, delay: 0.3 }}
                  >
                    <FaStar className="sparkle sparkle-2" />
                  </motion.div>
                  <motion.div
                    animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1.2, 0.8], rotate: [0, 180, 360] }}
                    transition={{ duration: 2, repeat: Infinity, delay: 0.6 }}
                  >
                    <FaStar className="sparkle sparkle-3" />
                  </motion.div>
                  <motion.div
                    animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1.2, 0.8], rotate: [0, 180, 360] }}
                    transition={{ duration: 2, repeat: Infinity, delay: 0.9 }}
                  >
                    <FaStar className="sparkle sparkle-4" />
                  </motion.div>
                </div>

                {/* Success text */}
                <motion.div 
                  className="success-text-container"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                >
                  <h2 className="text-success mb-3 success-title">Payment Successful!</h2>
                  <p className="lead text-muted success-message">
                    Your order has been placed successfully
                  </p>
                  <p className="transaction-id">
                    Transaction ID: <code>{transactionId}</code>
                  </p>
                </motion.div>

                {/* Buttons */}
                <motion.div 
                  className="d-grid gap-3 mt-4 button-container"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                >
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button 
                      variant="success" 
                      size="lg" 
                      onClick={handleViewOrders}
                      className="view-orders-btn w-100"
                    >
                      <FaReceipt className="me-2" />
                      View Orders
                    </Button>
                  </motion.div>
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button 
                      variant="outline-success" 
                      size="lg" 
                      onClick={handleContinueShopping}
                      className="continue-shopping-btn w-100"
                    >
                      <FaShoppingCart className="me-2" />
                      Continue Shopping
                    </Button>
                  </motion.div>
                </motion.div>
              </Card.Body>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </Container>
  );
};

export default PaymentSuccess;
